import { useState } from 'react';
import { Check, Loader2, Circle as CircleIcon } from 'lucide-react';
import type { TimelineStep } from '../lib/matterTimeline';

interface Props {
  steps: TimelineStep[];
  mode: 'staff' | 'client';
  /** Required for staff mode. Called when staff confirms a status change. */
  onChange?: (next: string) => Promise<void>;
  /** When true (staff mode only), surface terminal Refused/Withdrawn buttons
      under the timeline once the matter has progressed past pre-final-review. */
  showTerminalActions?: boolean;
  onTerminal?: (status: string) => Promise<void>;
}

/**
 * Renders the unified matter timeline. Steps come from
 * `computeTimelineSteps(ctx)` so client and staff views are guaranteed to
 * agree.
 *
 * Click model:
 *   - Read-only steps (data-derived, e.g. "Initial payment received")
 *     are never clickable.
 *   - Steps with a `targetStatus` are clickable for staff. Click → inline
 *     "Confirm change" card. Confirm → onChange(targetStatus).
 */
export default function LifecycleTimeline({
  steps,
  mode,
  onChange,
  showTerminalActions = false,
  onTerminal,
}: Props) {
  const [pending, setPending] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleClick = (step: TimelineStep) => {
    if (mode !== 'staff') return;
    if (!step.targetStatus) return;
    if (step.status === 'current' || step.status === 'done') return;
    setPending(step.targetStatus);
  };

  const handleConfirm = async () => {
    if (!pending || !onChange) return;
    setBusy(true);
    try {
      await onChange(pending);
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        {mode === 'staff' ? 'Lifecycle' : 'Application timeline'}
      </div>
      <ol className="space-y-1.5">
        {steps.map((step, i) => {
          const label = mode === 'staff' ? step.staffLabel : step.clientLabel;
          const desc = mode === 'staff' ? step.staffDesc : step.clientDesc;
          const showConfirm = pending === step.targetStatus && pending !== null;
          const isClickable = mode === 'staff' && !!step.targetStatus && step.status !== 'current';
          const isCurrent = step.status === 'current';
          const isDone = step.status === 'done';
          const isPending = step.status === 'pending';
          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => handleClick(step)}
                disabled={!isClickable || busy}
                className={`w-full text-left flex items-start gap-3 px-3 py-2 rounded-md transition-colors ${
                  isClickable ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'
                } ${isCurrent ? 'bg-[#EBF7FD]' : ''}`}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span
                  className={`shrink-0 mt-0.5 grid place-items-center w-5 h-5 rounded-full border ${
                    isCurrent
                      ? 'bg-[#2CABEA] border-[#2CABEA] text-white'
                      : isDone
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'bg-white border-slate-300 text-slate-400'
                  }`}
                  aria-hidden="true"
                >
                  {isDone
                    ? <Check size={11} />
                    : isCurrent
                      ? <span className="text-[10px] font-semibold">{i + 1}</span>
                      : <CircleIcon size={11} className="opacity-0" />
                  }
                </span>
                <span className="flex-1 min-w-0">
                  <span className={`block text-sm font-semibold ${isCurrent ? 'text-[#0B2341]' : isPending ? 'text-slate-500' : 'text-slate-700'}`}>
                    {label} {isCurrent && <span className="text-xs font-normal text-[#2CABEA] ml-1">· now</span>}
                  </span>
                  <span className={`block text-xs ${isPending ? 'text-slate-400' : 'text-slate-500'} leading-snug`}>{desc}</span>
                  {step.date && (
                    <span className="block text-[10px] text-slate-400 mt-0.5">
                      {new Date(step.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </span>
              </button>

              {showConfirm && (
                <div className="ml-8 mt-1 mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs space-y-2">
                  <div className="text-amber-900">
                    Change status to <strong>{step.staffLabel}</strong>?
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleConfirm}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-50"
                    >
                      {busy ? <Loader2 size={11} className="animate-spin" /> : null}
                      Confirm change
                    </button>
                    <button
                      type="button"
                      onClick={() => setPending(null)}
                      className="px-2 py-1.5 rounded-md text-slate-600 hover:bg-slate-100"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {showTerminalActions && onTerminal && (
        <div className="pt-2 border-t border-slate-200 mt-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Other outcomes</div>
          <div className="flex gap-2">
            <TerminalConfirmButton tone="red" label="Refused" target="Refused" busy={busy} setBusy={setBusy} onTerminal={onTerminal} />
            <TerminalConfirmButton tone="slate" label="Withdrawn" target="Withdrawn" busy={busy} setBusy={setBusy} onTerminal={onTerminal} />
          </div>
        </div>
      )}
    </div>
  );
}

function TerminalConfirmButton({
  tone, label, target, busy, setBusy, onTerminal,
}: {
  tone: 'red' | 'slate';
  label: string;
  target: string;
  busy: boolean;
  setBusy: (b: boolean) => void;
  onTerminal: (status: string) => Promise<void>;
}) {
  const [armed, setArmed] = useState(false);
  const cls = tone === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-600 hover:bg-slate-700';
  if (!armed) {
    return (
      <button
        type="button"
        onClick={() => setArmed(true)}
        disabled={busy}
        className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        Mark {label.toLowerCase()}…
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={async () => {
          setBusy(true);
          try { await onTerminal(target); } finally { setBusy(false); setArmed(false); }
        }}
        disabled={busy}
        className={`px-3 py-1.5 rounded-md text-white text-xs font-semibold ${cls} disabled:opacity-50`}
      >
        {busy ? <Loader2 size={11} className="animate-spin inline mr-1" /> : null}
        Confirm {label}
      </button>
      <button type="button" onClick={() => setArmed(false)} className="px-2 py-1.5 rounded-md text-xs text-slate-500 hover:bg-slate-100">
        Cancel
      </button>
    </div>
  );
}
